import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Stack, Typography } from '@mui/material';
import { LoadingWrapper } from 'components/custom';
import { Flow, handleErrorFallback, handleFlowError, ory, } from 'components/kratos';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setTitle } from 'utils/head';
import { HttpStatusCodes } from 'utils/http-status-codes';
import { ACTIVATION, REGISTRATION } from 'utils/links';
/**
 * Displays registration form.
 */
const Registration = () => {
    const { t } = useTranslation('auth');
    const [loading, setLoading] = React.useState(true);
    const [customerType, setCustomerType] = React.useState();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    useEffect(() => {
        setTitle(t('head.registration.title'));
    }, [t]);
    /*
     * The "flow" represents a registration process and contains
     * information about the form we need to render (e.g. username + password)
     */
    const [flow, setFlow] = useState();
    // Get ?flow=... from the URL
    const flowId = searchParams.get('flow');
    // In this effect we either initiate a new registration flow, or we fetch an existing registration flow.
    useEffect(() => {
        // If we already have a flow, do nothing.
        if (flow) {
            return;
        }
        setLoading(true);
        // If ?flow=.. was in the URL, we fetch it
        if (flowId) {
            ory
                .getRegistrationFlow({ id: String(flowId) })
                .then(({ data }) => {
                // We received the flow - let's use its data and render the form!
                setFlow(data);
            })
                .catch(handleFlowError(navigate, 'registration', setFlow, t))
                .catch(handleErrorFallback(t))
                .finally(() => {
                setLoading(false);
            });
            return;
        }
        // Otherwise we initialize it
        ory
            .createBrowserRegistrationFlow()
            .then(({ data }) => {
            setFlow(data);
        })
            .catch(handleFlowError(navigate, 'registration', setFlow, t))
            .catch(handleErrorFallback(t))
            .finally(() => {
            setLoading(false);
        });
    }, [flowId, navigate, flow, t]);
    const onSubmit = async (values) => {
        setLoading(true);
        /*
         * On submission, add the flow ID to the URL but do not navigate. This prevents the user loosing
         * his data when she/he reloads the page.
         */
        navigate(`${REGISTRATION}?flow=${String(flow?.id)}`, {
            replace: true,
        });
        return ory
            .updateRegistrationFlow({
            flow: String(flow?.id),
            updateRegistrationFlowBody: values,
        })
            .then(async () => {
            navigate(ACTIVATION);
            return Promise.resolve();
        })
            .catch(handleFlowError(navigate, 'registration', setFlow, t))
            .catch(async (err) => {
            // If the previous handler did not catch the error it's most likely a form validation error
            if (err.response?.status === HttpStatusCodes.BAD_REQUEST) {
                setFlow(err.response.data);
                return Promise.resolve();
            }
            return Promise.reject(err);
        })
            .catch(handleErrorFallback(t))
            .finally(() => {
            setLoading(false);
        });
    };
    return (_jsx(LoadingWrapper, { status: { loading }, children: _jsxs(Stack, { spacing: 2.5, sx: { width: '100%' }, children: [_jsx(Typography, { variant: "h5", color: "secondary.main", children: t('body.step.registration.heading1') }), _jsx(Flow, { onSubmit: onSubmit, flow: flow, templateName: "registration", context: { customerType, setCustomerType } })] }) }));
};
export default Registration;
